/**
 * PropertyNearbyPlaces Component
 *
 * Step 3 of the property video wizard: Display nearby places/amenities.
 * Shows results from Google Places API location analysis organized by category.
 */

import { useMemo } from 'react';
import { GraduationCap, Baby, ShoppingCart, Utensils, Bus, Pill, MapPin } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type {
  IPropertyNearbyPlacesProps,
  INearbyPlace,
  ICategoryConfig,
  IPlaceItemProps,
  ICategoryCardProps,
} from '@/@types/property';

const CATEGORY_CONFIGS: ICategoryConfig[] = [
  {
    key: 'schools',
    label: 'Schools',
    icon: <GraduationCap className="h-5 w-5" aria-hidden="true" />,
  },
  {
    key: 'kindergartens',
    label: 'Kindergartens',
    icon: <Baby className="h-5 w-5" aria-hidden="true" />,
  },
  {
    key: 'supermarkets',
    label: 'Supermarkets',
    icon: <ShoppingCart className="h-5 w-5" aria-hidden="true" />,
  },
  {
    key: 'restaurants',
    label: 'Restaurants',
    icon: <Utensils className="h-5 w-5" aria-hidden="true" />,
  },
  {
    key: 'busStops',
    label: 'Bus Stops',
    icon: <Bus className="h-5 w-5" aria-hidden="true" />,
  },
  {
    key: 'pharmacies',
    label: 'Pharmacies',
    icon: <Pill className="h-5 w-5" aria-hidden="true" />,
  },
];

const MAX_PLACES_PER_CATEGORY = 5;

/**
 * Format distance: <1000m show as "Xm", >=1000m show as "X.Xkm"
 */
function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters)}m`;
  }
  return `${(meters / 1000).toFixed(1)}km`;
}

/**
 * Sort places by distance (closest first) and limit to max count
 */
function sortAndLimitPlaces(places: INearbyPlace[]): INearbyPlace[] {
  return [...places].sort((a, b) => a.distance - b.distance).slice(0, MAX_PLACES_PER_CATEGORY);
}

/**
 * Helper component to display a single place item
 */
function PlaceItem({ place }: IPlaceItemProps) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-md border bg-background p-2">
      <span className="truncate text-sm" title={place.name}>
        {place.name}
      </span>
      <Badge variant="secondary" className="shrink-0">
        {formatDistance(place.distance)}
      </Badge>
    </div>
  );
}

/**
 * Helper component to display a category card
 */
function CategoryCard({ config, places }: ICategoryCardProps) {
  const sortedPlaces = useMemo(() => sortAndLimitPlaces(places), [places]);
  const hasPlaces = sortedPlaces.length > 0;

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {config.icon}
          {config.label}
          {hasPlaces && (
            <Badge variant="outline" className="ml-auto">
              {places.length}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {hasPlaces ? (
          <div className="space-y-2">
            {sortedPlaces.map(place => (
              <PlaceItem key={`${place.name}-${place.distance}`} place={place} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No {config.label.toLowerCase()} found nearby.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * PropertyNearbyPlaces Component
 *
 * Displays nearby places organized by category in a responsive grid.
 * Each category shows up to 5 places sorted by distance.
 */
function PropertyNearbyPlaces({ nearbyPlaces }: IPropertyNearbyPlacesProps) {
  const hasAnyPlaces = useMemo(() => {
    if (!nearbyPlaces) {
      return false;
    }
    return CATEGORY_CONFIGS.some(config => nearbyPlaces[config.key]?.length > 0);
  }, [nearbyPlaces]);

  return (
    <Card className="w-full max-w-4xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" aria-hidden="true" />
          Nearby Places
        </CardTitle>
        <CardDescription>
          Review the nearby amenities and points of interest around this property.
        </CardDescription>
      </CardHeader>

      <CardContent>
        {!nearbyPlaces || !hasAnyPlaces ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <MapPin className="mb-4 h-12 w-12 text-muted-foreground" aria-hidden="true" />
            <h3 className="mb-2 text-lg font-medium">No Nearby Places Found</h3>
            <p className="max-w-sm text-sm text-muted-foreground">
              Location analysis did not find any nearby places for this property. You can still
              continue to the next step.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {CATEGORY_CONFIGS.map(config => (
              <CategoryCard
                key={config.key}
                config={config}
                places={nearbyPlaces[config.key] || []}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export { PropertyNearbyPlaces };
